import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Image as ImageIcon, Link2, Loader2, AlertCircle, X, Maximize2, ChevronLeft, ChevronRight,
  Upload, Trash2, GripVertical, Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button/Button';
import { VStack } from '@/components/ui/Stack';
import { LocationAutocomplete, type LocationData } from '@/components/ui/LocationAutocomplete/LocationAutocomplete';
import { useGooglePlacePhotos } from '@/hooks/useGooglePlacePhotos';
import { PlacesService } from '@/services/PlacesService';
import { VenuePhotoService, type VenuePhoto } from '@/services/VenuePhotoService';
import { AttachmentsService } from '@/services/AttachmentsService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotificationStore } from '@/stores/notificationStore';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, useSortable, rectSortingStrategy, sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/** A draggable/sortable custom-photo tile (drag to reorder). */
function SortablePhoto({ photo, index, onDelete, onOpen }: {
  photo: VenuePhoto;
  index: number;
  onDelete: (id: string) => void;
  onOpen: (index: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: photo.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group rounded-lg overflow-hidden bg-gray-100 aspect-video"
    >
      <img
        src={photo.url}
        alt={`Custom photo ${index + 1}`}
        className="w-full h-full object-cover cursor-pointer"
        loading="lazy"
        onClick={() => onOpen(index)}
      />
      {/* Controls overlay */}
      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Drag handle — type="button" is REQUIRED: this panel renders inside the
            Edit Venue <form>, and a typeless button defaults to type="submit",
            so grabbing a photo used to submit the form and close the modal. */}
        <button
          type="button"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-md transition-colors cursor-grab active:cursor-grabbing touch-none"
          title="Drag to reorder"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(photo.id); }}
          className="p-1.5 bg-red-600/80 hover:bg-red-600 text-white rounded-md transition-colors"
          title="Remove photo"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Sort order badge */}
      <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded">
        {index + 1}
      </div>
    </div>
  );
}

interface VenueImagesPanelProps {
  venueId: string;
  /** Currently connected Google Place ID (from the venue record or the parent form state). */
  placeId?: string | null;
  /** Called after the Google Place connection is connected/disconnected so parents can stay in sync. */
  onPlaceIdChanged?: (placeId: string | null) => void;
  className?: string;
}

/**
 * Shared venue image-management UI (custom photos + Google Place connection + Google photos
 * + lightbox). Used by the Edit Venue modal and the venue details modal so images can be
 * managed from either place.
 */
export function VenueImagesPanel({ venueId, placeId, onPlaceIdChanged, className }: VenueImagesPanelProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isGalleryClosing, setIsGalleryClosing] = useState(false);
  const [shouldRenderGallery, setShouldRenderGallery] = useState(false);
  const [isGalleryMounted, setIsGalleryMounted] = useState(false);
  const lightboxRef = useRef<HTMLDivElement>(null);

  const [customPhotos, setCustomPhotos] = useState<VenuePhoto[]>([]);
  const [isLoadingCustom, setIsLoadingCustom] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [isAddingUrl, setIsAddingUrl] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Local view of the connected place: starts from the prop, then follows connect/disconnect
  const [connectedPlaceId, setConnectedPlaceId] = useState<string | null>(placeId ?? null);
  const [placeSearchValue, setPlaceSearchValue] = useState('');

  useEffect(() => {
    setConnectedPlaceId(placeId ?? null);
    setPlaceSearchValue('');
  }, [placeId]);

  const queryClient = useQueryClient();
  const { push: addNotification } = useNotificationStore();
  const placesService = new PlacesService();
  const venuePhotoService = new VenuePhotoService();
  const attachmentsService = new AttachmentsService();

  // Load custom photos on mount (panel only renders while its tab is active)
  const loadCustomPhotos = useCallback(async () => {
    if (!venueId) return;
    setIsLoadingCustom(true);
    try {
      const photos = await venuePhotoService.listPhotos(venueId);
      setCustomPhotos(photos);
    } catch (err) {
      console.error('Failed to load custom photos:', err);
    } finally {
      setIsLoadingCustom(false);
    }
  }, [venueId]);

  useEffect(() => {
    loadCustomPhotos();
  }, [loadCustomPhotos]);

  // Upload handler
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || !venueId) return;
    setIsUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) {
          addNotification({ type: 'error', title: 'Invalid file', message: `${file.name} is not an image` });
          continue;
        }
        // Upload to R2 via attachments service
        const attachment = await attachmentsService.upload(file, { title: file.name });
        // Link to venue
        await venuePhotoService.addPhoto(venueId, attachment.id);
      }
      addNotification({ type: 'success', title: 'Photos uploaded', message: 'Custom photos added successfully' });
      await loadCustomPhotos();
    } catch (err) {
      addNotification({ type: 'error', title: 'Upload failed', message: err instanceof Error ? err.message : 'Failed to upload photos' });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Delete custom photo
  const handleDeleteCustomPhoto = async (photoId: string) => {
    if (!venueId) return;
    try {
      await venuePhotoService.removePhoto(venueId, photoId);
      setCustomPhotos(prev => prev.filter(p => p.id !== photoId));
      addNotification({ type: 'success', title: 'Photo removed', message: 'Photo has been removed.' });
    } catch (err) {
      addNotification({ type: 'error', title: 'Delete failed', message: 'Failed to remove photo' });
    }
  };

  // Add a custom photo by external URL
  const handleAddFromUrl = async () => {
    if (!venueId) return;
    const url = urlInput.trim();
    if (!/^https?:\/\/.+/i.test(url)) {
      addNotification({ type: 'error', title: 'Invalid URL', message: 'Enter a valid http(s) image URL' });
      return;
    }
    setIsAddingUrl(true);
    try {
      await venuePhotoService.addPhotoFromUrl(venueId, url);
      addNotification({ type: 'success', title: 'Photo added', message: 'Photo added from URL' });
      setUrlInput('');
      setShowUrlInput(false);
      await loadCustomPhotos();
    } catch (err) {
      addNotification({ type: 'error', title: 'Add failed', message: err instanceof Error ? err.message : 'Failed to add photo from URL' });
    } finally {
      setIsAddingUrl(false);
    }
  };

  // Drag-and-drop reorder
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handlePhotosDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!venueId || !over || active.id === over.id) return;
    const oldIndex = customPhotos.findIndex(p => p.id === active.id);
    const newIndex = customPhotos.findIndex(p => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(customPhotos, oldIndex, newIndex);
    setCustomPhotos(reordered);
    try {
      await venuePhotoService.reorderPhotos(venueId, reordered.map(p => p.id));
    } catch (err) {
      await loadCustomPhotos();
      addNotification({ type: 'error', title: 'Reorder failed', message: 'Failed to reorder photos' });
    }
  };

  // Handle gallery mount/unmount with animation
  useEffect(() => {
    if (selectedImageIndex !== null) {
      setShouldRenderGallery(true);
      setIsGalleryClosing(false);
      requestAnimationFrame(() => {
        setIsGalleryMounted(true);
      });
    } else if (shouldRenderGallery) {
      setIsGalleryClosing(true);
      setIsGalleryMounted(false);
      const timer = setTimeout(() => {
        setShouldRenderGallery(false);
        setIsGalleryClosing(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [selectedImageIndex, shouldRenderGallery]);

  // Fetch Google photos for the connected place (panel is mounted only while its tab is active)
  const { photos, isLoading: isLoadingPhotos, error: photosError, refetch: refetchPhotos } = useGooglePlacePhotos(
    connectedPlaceId,
    true
  );

  // Focus lightbox when opened for keyboard navigation
  useEffect(() => {
    if (selectedImageIndex !== null && lightboxRef.current && !isGalleryClosing) {
      lightboxRef.current.focus();
    }
  }, [selectedImageIndex, isGalleryClosing]);

  // Keyboard navigation while the lightbox is open.
  // Handled with a native capture-phase document listener so the parent modal's own
  // Escape handler (bubble phase) never also closes the modal behind the lightbox —
  // React synthetic handlers on portal content can't reliably stop it.
  useEffect(() => {
    if (selectedImageIndex === null) return;
    const total = customPhotos.length + photos.length;
    const onKey = (e: KeyboardEvent) => {
      if (isGalleryClosing) return;
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        setSelectedImageIndex(null);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setSelectedImageIndex(i => (i !== null && i > 0 ? i - 1 : i));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setSelectedImageIndex(i => (i !== null && i < total - 1 ? i + 1 : i));
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [selectedImageIndex, isGalleryClosing, customPhotos.length, photos.length]);

  // Mutation to update venue place_id
  const updatePlaceIdMutation = useMutation({
    mutationFn: async (newPlaceId: string | null) => {
      await placesService.updateVenuePlaceId(venueId, newPlaceId);
    },
    onSuccess: (_, newPlaceId) => {
      setConnectedPlaceId(newPlaceId);
      if (!newPlaceId) setPlaceSearchValue('');
      onPlaceIdChanged?.(newPlaceId);
      addNotification({
        type: 'success',
        title: 'Place ID Updated',
        message: newPlaceId ? 'Google Place ID has been connected successfully' : 'Google Place ID has been removed'
      });
      queryClient.invalidateQueries({ queryKey: ['venues'] });
      if (newPlaceId) {
        refetchPhotos();
      }
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Update Failed',
        message: error instanceof Error ? error.message : 'Failed to update Google Place ID'
      });
    }
  });

  const handlePlaceSelect = (location: LocationData) => {
    setPlaceSearchValue(location.formatted_address);
    updatePlaceIdMutation.mutate(location.place_id);
  };

  const handleDisconnectPlace = () => {
    updatePlaceIdMutation.mutate(null);
    setPlaceSearchValue('');
  };

  return (
    <div className={className}>
      <VStack gap={6}>
        {/* ── Section 1: Custom Photos ── */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium flex items-center gap-2">
                <Upload className="w-4 h-4 text-primary-600" />
                Custom Photos
                {customPhotos.length > 0 && (
                  <span className="text-xs text-muted-foreground font-normal">
                    ({customPhotos.length})
                  </span>
                )}
              </h4>
              <p className="text-xs text-muted-foreground mt-1">
                {customPhotos.length > 0
                  ? 'These photos are used everywhere instead of Google Photos'
                  : 'Upload custom photos to override Google Places photos'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleFileUpload(e.target.files)}
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                leftIcon={<Link2 className="w-4 h-4" />}
                onClick={() => setShowUrlInput(v => !v)}
              >
                Add from URL
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                leftIcon={isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload Photos'}
              </Button>
            </div>
          </div>

          {/* Add-from-URL input row */}
          {showUrlInput && (
            <div className="flex items-center gap-2">
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddFromUrl(); } }}
                placeholder="https://example.com/photo.jpg"
                autoFocus
                className="flex-1 text-sm border rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <Button type="button" size="sm" onClick={handleAddFromUrl} disabled={isAddingUrl || !urlInput.trim()}>
                {isAddingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => { setShowUrlInput(false); setUrlInput(''); }}>
                Cancel
              </Button>
            </div>
          )}

          {/* Drop zone when no custom photos */}
          {customPhotos.length === 0 && !isLoadingCustom && (
            <div
              className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-primary-300 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary-400', 'bg-primary-50'); }}
              onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50'); }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50');
                handleFileUpload(e.dataTransfer.files);
              }}
            >
              <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Drag & drop images here, or click to browse
              </p>
            </div>
          )}

          {isLoadingCustom && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          )}

          {/* Custom photos grid — drag any photo to reorder */}
          {customPhotos.length > 0 && (
            <>
              <p className="text-xs text-muted-foreground -mt-1 flex items-center gap-1">
                <GripVertical className="w-3 h-3" /> Drag photos to reorder
              </p>
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handlePhotosDragEnd}>
                <SortableContext items={customPhotos.map(p => p.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {customPhotos.map((photo, index) => (
                      <SortablePhoto
                        key={photo.id}
                        photo={photo}
                        index={index}
                        onDelete={handleDeleteCustomPhoto}
                        onOpen={setSelectedImageIndex}
                      />
                    ))}
                    {/* Add more button */}
                    <div
                      className="border-2 border-dashed border-gray-200 rounded-lg aspect-video flex flex-col items-center justify-center cursor-pointer hover:border-primary-300 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary-400', 'bg-primary-50'); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50'); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50');
                        handleFileUpload(e.dataTransfer.files);
                      }}
                    >
                      <Plus className="w-6 h-6 text-gray-400" />
                      <span className="text-xs text-muted-foreground mt-1">Add more</span>
                    </div>
                  </div>
                </SortableContext>
              </DndContext>
            </>
          )}
        </div>

        {/* ── Section 2: Google Place Connection & Photos ── */}
        <div className="pt-4 border-t space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <Link2 className="w-4 h-4 text-primary-600" />
              Google Place Connection
              {customPhotos.length > 0 && (
                <span className="text-xs text-muted-foreground font-normal ml-1">
                  (overridden by custom photos above)
                </span>
              )}
            </h4>
            <p className="text-xs text-muted-foreground mb-4">
              Connect this venue to its Google Place to automatically load photos
            </p>

            {connectedPlaceId ? (
              <div className="space-y-3">
                <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-primary-900 mb-1">Connected Place ID</p>
                      <p className="text-xs text-primary-700 font-mono break-all">
                        {connectedPlaceId}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleDisconnectPlace}
                      disabled={updatePlaceIdMutation.isPending}
                      className="flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {updatePlaceIdMutation.isPending && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Updating...
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <LocationAutocomplete
                  value={placeSearchValue}
                  onChange={setPlaceSearchValue}
                  onLocationSelect={handlePlaceSelect}
                  placeholder="Search for this venue on Google Places..."
                  types="establishment"
                  disabled={updatePlaceIdMutation.isPending}
                />
                {updatePlaceIdMutation.isPending && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Connecting...
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Google Photo Gallery */}
        <div className={cn("pt-4 border-t", customPhotos.length > 0 && "opacity-60")}>
          <h4 className="text-sm font-medium mb-4 flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-primary-600" />
            Google Photos
            {photos.length > 0 && (
              <span className="text-xs text-muted-foreground font-normal">
                ({photos.length})
              </span>
            )}
          </h4>

          {isLoadingPhotos ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mb-3" />
              <p className="text-sm text-muted-foreground">Loading photos...</p>
            </div>
          ) : photosError ? (
            <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
              <AlertCircle className="w-8 h-8 text-warning-600 mb-3" />
              <p className="text-sm text-muted-foreground mb-2">Failed to load photos</p>
              <p className="text-xs text-muted-foreground mb-4">{photosError}</p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => refetchPhotos()}
              >
                Retry
              </Button>
            </div>
          ) : photos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
              <ImageIcon className="w-12 h-12 text-gray-400 mb-3" />
              <p className="text-sm text-muted-foreground mb-1">
                {connectedPlaceId
                  ? 'No photos available for this place'
                  : 'Connect a Google Place ID to view photos'}
              </p>
              {connectedPlaceId ? (
                <p className="text-xs text-muted-foreground">
                  This place may not have photos in Google Places
                </p>
              ) : null}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {photos.map((photo, index) => (
                <div
                  key={photo.photo_reference}
                  className="relative group cursor-pointer rounded-lg overflow-hidden bg-gray-100 aspect-video"
                  onClick={() => setSelectedImageIndex(customPhotos.length + index)}
                >
                  <img
                    src={photo.url}
                    alt={`Venue photo ${index + 1}`}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <Maximize2 className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Photo Attributions */}
          {photos.length > 0 && photos[0]?.html_attributions && photos[0].html_attributions.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                Photos from Google Places. Attribution required per Google's terms.
              </p>
            </div>
          )}
        </div>
      </VStack>

      {/* Image Lightbox Modal - Rendered via Portal to be independent of the parent modal */}
      {/* Combined photos: custom first, then Google */}
      {(() => {
        const allLightboxPhotos = [
          ...customPhotos.map(p => ({ url: p.url, photo_reference: p.id })),
          ...photos.map(p => ({ url: p.url, photo_reference: p.photo_reference })),
        ];
        const currentLightboxPhoto = selectedImageIndex !== null ? allLightboxPhotos[selectedImageIndex] : null;
        return shouldRenderGallery && selectedImageIndex !== null && currentLightboxPhoto && typeof window !== 'undefined' && createPortal(
        <div
          ref={lightboxRef}
          className={cn(
            'fixed inset-0 z-[9999] bg-black w-screen h-screen flex items-center justify-center outline-none',
            'transform transition-all duration-200 ease-out',
            isGalleryClosing ? 'opacity-0' : 'opacity-100'
          )}
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isGalleryClosing) {
              setSelectedImageIndex(null);
            }
          }}
          tabIndex={0}
        >
          <div className={cn(
            'relative w-full h-full flex items-center justify-center p-4',
            'transform transition-all duration-200 ease-out',
            isGalleryClosing
              ? 'opacity-0 scale-95 animate-scale-out'
              : isGalleryMounted
                ? 'opacity-100 scale-100'
                : 'opacity-0 scale-95 animate-scale-in'
          )}>
            {/* Close Button */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImageIndex(null);
              }}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-20 p-2 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Left Navigation Area - Full Height */}
            {selectedImageIndex > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(selectedImageIndex - 1);
                }}
                className="absolute left-0 top-0 bottom-0 w-1/4 md:w-1/3 flex items-center justify-start pl-8 text-white hover:bg-white/5 transition-colors z-10 cursor-pointer group"
                aria-label="Previous image"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-black/50 group-hover:bg-black/70 transition-colors">
                  <ChevronLeft className="w-8 h-8" />
                </div>
              </button>
            )}

            {/* Right Navigation Area - Full Height */}
            {selectedImageIndex < allLightboxPhotos.length - 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(selectedImageIndex + 1);
                }}
                className="absolute right-0 top-0 bottom-0 w-1/4 md:w-1/3 flex items-center justify-end pr-8 text-white hover:bg-white/5 transition-colors z-10 cursor-pointer group"
                aria-label="Next image"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-black/50 group-hover:bg-black/70 transition-colors">
                  <ChevronRight className="w-8 h-8" />
                </div>
              </button>
            )}

            {/* Image */}
            <img
              src={currentLightboxPhoto.url.replace('maxwidth=800', 'maxwidth=1600')}
              alt={`Venue photo ${selectedImageIndex + 1}`}
              className="object-contain"
              style={{
                maxWidth: '100vw',
                maxHeight: '100vh',
                width: 'auto',
                height: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            />

            {/* Image Counter */}
            {allLightboxPhotos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full z-20">
                {selectedImageIndex + 1} / {allLightboxPhotos.length}
              </div>
            )}
          </div>
        </div>,
        document.body
      );
      })()}
    </div>
  );
}
